import type { ChatMessage } from '@auw/workspace-utils';

/**
 * Merge authoritative history with the richer live message already rendered
 * in this tab.
 *
 * The REST history owns persisted message fields.  Live-only UI state (most
 * importantly the timeline collected from SSE) must not be erased merely
 * because an older server row has no corresponding metadata yet.
 */
export function mergeHydratedMessage(
  persisted: ChatMessage,
  live: ChatMessage,
): ChatMessage {
  return {
    ...live,
    ...persisted,
    streamEvents:
      persisted.streamEvents.length > 0
        ? persisted.streamEvents
        : live.streamEvents,
    thinkingContent: persisted.thinkingContent ?? live.thinkingContent,
    isThinking: persisted.isThinking ?? live.isThinking,
    widgets: persisted.widgets ?? live.widgets,
    completedData: persisted.completedData ?? live.completedData,
    error: persisted.error ?? live.error,
    // A history fetch may race an active stream.  A persisted row is always
    // non-streaming, but it must not terminate the in-flight local message.
    isStreaming: live.isStreaming || persisted.isStreaming,
  };
}

/**
 * Reconcile one message_created event with its optimistic client message.
 *
 * The backend assigns a canonical id and echoes client_message_id.  Treating
 * those as two independent messages creates duplicate React keys and can make
 * the live timeline appear to vanish when the canonical row wins rendering.
 */
export function reconcileCreatedMessage(
  messages: ChatMessage[],
  incoming: ChatMessage,
  clientMessageId: string | null,
): ChatMessage[] {
  const matchingIndexes: number[] = [];
  messages.forEach((message, index) => {
    if (
      message.id === incoming.id
      || (clientMessageId !== null && message.id === clientMessageId)
    ) {
      matchingIndexes.push(index);
    }
  });

  if (matchingIndexes.length === 0) {
    return [...messages, incoming];
  }

  const preferredIndex =
    matchingIndexes.find(
      (index) => messages[index].streamEvents.length > 0,
    ) ?? matchingIndexes[0];
  const reconciled = {
    ...mergeHydratedMessage(incoming, messages[preferredIndex]),
    id: incoming.id,
  };
  const firstIndex = matchingIndexes[0];
  const matched = new Set(matchingIndexes);
  const next: ChatMessage[] = [];
  messages.forEach((message, index) => {
    if (index === firstIndex) {
      next.push(reconciled);
      return;
    }
    if (!matched.has(index)) next.push(message);
  });
  return next;
}

/** Merge REST history and current messages without dropping live timelines. */
export function mergeHydratedMessages(
  persistedMessages: ChatMessage[],
  liveMessages: ChatMessage[],
): ChatMessage[] {
  const liveById = new Map(liveMessages.map((message) => [message.id, message]));
  const seen = new Set<string>();
  const merged = persistedMessages.map((persisted) => {
    seen.add(persisted.id);
    const live = liveById.get(persisted.id);
    return live ? mergeHydratedMessage(persisted, live) : persisted;
  });

  liveMessages.forEach((message) => {
    if (!seen.has(message.id)) merged.push(message);
  });
  return merged;
}
